// src/components/ApplicationsDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import './Viewapplication.css';
import AdminSidebar from '../Adminsidebar/AdminSidebar';

// --- SVG Icons ---
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;

const API_URL = 'https://api.hitkarihospital.com:5005/api';
const BASE_URL = 'https://api.hitkarihospital.com:5005'; // For accessing uploaded files

const ApplicationsDashboard = () => {
    const [applications, setApplications] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [selectedApp, setSelectedApp] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchApplications = useCallback(async (page = 1) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_URL}/applications?page=${page}&limit=10`);
            if (!response.ok) throw new Error('Failed to fetch applications.');
            const data = await response.json();
            setApplications(data.applications);
            setPagination({ currentPage: data.currentPage, totalPages: data.totalPages });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchApplications(1);
    }, [fetchApplications]);

     // --- 2. ADD DELETE HANDLER FUNCTION ---
    const handleDelete = async (appId) => {
        // Add a confirmation dialog to prevent accidental deletion
        if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/applications/${appId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete the application.');
            }
            
            // On successful deletion, remove the application from the local state
            // This provides instant feedback to the user without a full page reload
            setApplications(prevApplications => prevApplications.filter(app => app._id !== appId));
            
            // Optional: Show a success notification
            alert('Application deleted successfully.');

        } catch (err) {
            setError(err.message);
            alert(`Error: ${err.message}`);
        }
    };

  

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchApplications(newPage);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    return (
        <>
          <AdminSidebar />
        <div className="dashboard-container">
            <header className="dashboard-header">
                <h1>Job Applications</h1>
                <p>Review and manage all submitted applications.</p>
            </header>

            {isLoading && <div className="loader"></div>}
            {error && <p className="error-message">{error}</p>}
            
            {!isLoading && !error && (
                <div className="table-wrapper">
                    <table className="applications-table">
                        <thead>
                            <tr>
                                <th>Applicant Name</th>
                                <th>Applied For</th>
                                <th>Contact</th>
                                <th>Date Submitted</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map(app => (
                                <tr key={app._id}>
                                    <td data-label="Applicant Name">{app.firstName} {app.lastName}</td>
                                    <td data-label="Applied For">{app.jobRole}</td>
                                    <td data-label="Contact">{app.email}</td>
                                    <td data-label="Date Submitted">{formatDate(app.submissionDate)}</td>
                                    <td data-label="Actions">
                                        <button className="action-btn view" onClick={() => setSelectedApp(app)}>
                                            <EyeIcon /> View
                                        </button>
                                         <button className="action-btn delete" onClick={() => handleDelete(app._id)}>
                                            <DeleteIcon /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls */}
            <div className="pagination-controls">
                <button 
                    onClick={() => handlePageChange(pagination.currentPage - 1)} 
                    disabled={pagination.currentPage === 1}
                >
                    « Previous
                </button>
                <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
                <button 
                    onClick={() => handlePageChange(pagination.currentPage + 1)} 
                    disabled={pagination.currentPage === pagination.totalPages}
                >
                    Next »
                </button>
            </div>

            {/* Application Details Modal */}
            {selectedApp && (
                <div className="modal-overlay" onClick={() => setSelectedApp(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setSelectedApp(null)}>×</button>
                        <h2>{selectedApp.firstName} {selectedApp.lastName}</h2>
                        <p className="modal-subtitle">Application for <strong>{selectedApp.jobRole}</strong></p>

                        <div className="modal-details-grid">
                            <div className="detail-item"><span>Email</span>{selectedApp.email}</div>
                            <div className="detail-item"><span>Contact No.</span>{selectedApp.contactNo}</div>
                            <div className="detail-item"><span>Date of Birth</span>{formatDate(selectedApp.dob)}</div>
                            <div className="detail-item"><span>Gender</span>{selectedApp.gender || 'N/A'}</div>
                            <div className="detail-item"><span>Marital Status</span>{selectedApp.maritalStatus || 'N/A'}</div>
                            <div className="detail-item"><span>Working Status</span>{selectedApp.workingStatus}</div>
                        </div>
                        <div className="detail-item full-width">
                            <span>Address</span>
                            {selectedApp.address || 'N/A'}
                        </div>

                        <a 
                            href={`${BASE_URL}/${selectedApp.resumePath.replace(/\\/g, '/')}`}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="resume-btn"
                        >
                            <DownloadIcon /> View / Download Resume
                        </a>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default ApplicationsDashboard;